import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import styled from "styled-components";

import CardContainer from "./CardContainer";
import PileContainer from "./PileContainer";
import Magic10Animation from "./Magic10Animation";
import WinAnimation from "./WinAnimation";
import GameOverAnimation from "./GameOverAnimation";
import Button from "./Button";
import { ConfirmPopup, DisconnectedPlayersPopup } from "./Popup";
import Log from "./Log";
import PlayerOrder from "./PlayerOrder";
import Banners from "./Banners";
import Game from "./Types/Game";
import PileType from "./Types/Pile";
import * as api from "./Api";

import {
  getIsEndTurnButtonEnabled,
  LEGACY_convertToPiles,
  LEGACY_convertCardsPlayedByUser,
  getIsDrawOnlyOneCardOnTopInAnyPile,
  getAvailablePilesPerCard,
  isGameOver,
} from "./utils";

import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { useSoundContext } from "./Hooks/useGameSound";
import DragPreview from "./DragPreview";

//copy-pasted from https://blog.logrocket.com/drag-and-drop-in-react/
//simple way to check whether the device support touch (it doesn't check all fallback, it supports only modern browsers)
const isTouchDevice = () => {
  if ("ontouchstart" in window) {
    return true;
  }
  return false;
};

// Assigning backend based on touch support on the device
const backendForDND = isTouchDevice() ? TouchBackend : HTML5Backend;

interface Props {
  game: Game;
  name: string;
  onRemovePlayer: (game: Game, name: string) => void;
  isSpectating: boolean;
}

const Board = styled.div`
  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-direction: column;
    min-height: 100vh;
    justify-content: center;
    position: absolute;
    margin: 0 auto;
    width: 100vw;
    z-index: 1;
  }
`;

const getNumberOfCardsInDeck = (game: Game) => game.deck.length;

const getMyPlayer = (
  game: Game,
  name: string,
  isSpectating: boolean,
  spectating?: string
) => {
  if (isSpectating) {
    const player = spectating
      ? game.players.find((p) => p.name === spectating)
      : game.players.find((p) => p.isPlayersTurn);
    return player ?? game.players[0];
  }
  return game.players.find((p) => p.name === name);
};

const getIsMyTurn = (game: Game, name: string, isSpectating: boolean) =>
  getMyPlayer(game, name, isSpectating)?.isPlayersTurn;

const getActivePlayerName = (game: Game) =>
  game.players.find((p) => p.isPlayersTurn)?.name;

const getIsGameWithoutActivePlayer = (game: Game) =>
  !game.players.some((p) => p.isPlayersTurn);

const GameBoard = ({ game, name, onRemovePlayer, isSpectating }: Props) => {
  const { playSound } = useSoundContext();
  const [availablePilesPerCard, setAvailablePilesPerCard] = React.useState<
    | {
        [c: number]: PileType[];
      }
    | undefined
  >(undefined);
  const [selectedCard, setSelectedCard] = useState<number>();
  const [spectating, setSpectating] = useState<string>();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);

  const { players, rules: gameRule, isTimeLimitEnabled, playedCards } = game;

  const isMyTurn = getIsMyTurn(game, name, isSpectating);
  const myPlayer = getMyPlayer(game, name, isSpectating, spectating);
  const cardsPlayedThisTurn = useMemo(
    () => myPlayer?.cardsPlayedThisTurn ?? [],
    [myPlayer]
  );
  const activePlayerName = getActivePlayerName(game);
  const cards = useMemo(() => myPlayer?.cards ?? [], [myPlayer]);

  if (!myPlayer) {
    throw new Error("Unable to find user with name " + name);
  }

  const isEndTurnButtonEnabled = useMemo(
    () =>
      isMyTurn &&
      (cards.length === 0 ||
        getIsEndTurnButtonEnabled(
          gameRule,
          LEGACY_convertToPiles(playedCards),
          getNumberOfCardsInDeck(game) || 0,
          game.id,
          LEGACY_convertCardsPlayedByUser(cardsPlayedThisTurn)
        )),
    [isMyTurn, cards, gameRule, playedCards, game, cardsPlayedThisTurn]
  );

  useEffect(() => {
    const newAvailableCards = getAvailablePilesPerCard(
      gameRule,
      LEGACY_convertToPiles(playedCards),
      cards,
      game.id,
      LEGACY_convertCardsPlayedByUser(cardsPlayedThisTurn)
    );
    setAvailablePilesPerCard(newAvailableCards);

    // Determine if game is over
    if (
      game.state === "started" &&
      isGameOver(newAvailableCards, isEndTurnButtonEnabled, isMyTurn)
    ) {
      api.lostGame(game);
    }
    if (game.state === "won") {
      playSound("gameWon");
    }
    if (game.state === "lost") {
      playSound("gameOver");
    }
  }, [
    game,
    isMyTurn,
    isEndTurnButtonEnabled,
    cardsPlayedThisTurn,
    gameRule,
    playedCards,
    cards,
    playSound,
  ]);

  const onShowInterest = (pileIndex: number) => {
    api.showInterestInPile(game, myPlayer.name, pileIndex);
  };

  const onClaimTurnClick = () => {
    api.claimTurn(game, myPlayer.name);
  };

  const onEndTurnClick = () => {
    api
      .endTurn(
        game,
        getIsDrawOnlyOneCardOnTopInAnyPile(
          game.rules,
          LEGACY_convertToPiles(playedCards),
          game.id
        )
      )
      .then(() => {
        setSelectedCard(undefined);
      });
  };

  const handleCardPlayed = (pileIndex: number) => {
    if (selectedCard !== undefined) {
      api
        .playCard(game, name, pileIndex, selectedCard)
        .then(() => setSelectedCard(undefined));
    }
  };

  const handleAutomaticCardPlayed = () => {
    if (isEndTurnButtonEnabled) {
      onEndTurnClick();
    } else if (game.state === "started") {
      const pilesWithAvailableMoves =
        availablePilesPerCard &&
        Object.fromEntries(
          Object.entries(availablePilesPerCard).filter(([k, v]) => v.length > 0)
        );
      if (pilesWithAvailableMoves !== undefined) {
        const cards = Object.keys(pilesWithAvailableMoves).map((k) =>
          Number.parseInt(k)
        );
        const randomCard = cards[Math.floor(Math.random() * cards.length)];
        const availablePilesForRandomCard = pilesWithAvailableMoves[randomCard];
        const randomPile =
          availablePilesForRandomCard[
            Math.floor(Math.random() * availablePilesForRandomCard.length)
          ].index;

        api.playCard(game, name, randomPile, randomCard);
      }
    }

    setSelectedCard(undefined);
  };

  const handleSpectatorChange = (name: string) =>
    spectating === name ? setSpectating(undefined) : setSpectating(name);

  const handleCardIsBeingDragged = (i: number) => () => {
    return (
      isMyTurn &&
      (selectedCard === i ? setSelectedCard(undefined) : setSelectedCard(i))
    );
  };

  const handleCardIsNotBeingDragged = () => {
    setSelectedCard(undefined);
  };

  const handleOnEndGameClick = () => setShowConfirm(true);
  const handleOnEndGameFoRealz = () => {
    api.endGame(game);

    setShowConfirm(false);
  };

  const interestedPlayers = [
    {
      pileIndex: 1,
      players: game.players.filter(
        (p) => p.interest && p.interest.pileIndex === 1
      ),
    },
    {
      pileIndex: 2,
      players: game.players.filter(
        (p) => p.interest && p.interest.pileIndex === 2
      ),
    },
    {
      pileIndex: 3,
      players: game.players.filter(
        (p) => p.interest && p.interest.pileIndex === 3
      ),
    },
    {
      pileIndex: 4,
      players: game.players.filter(
        (p) => p.interest && p.interest.pileIndex === 4
      ),
    },
  ];

  return (
    <Board>
      <div>
        {activePlayerName && (
          <Log
            playedCards={playedCards}
            activePlayerName={activePlayerName}
          ></Log>
        )}
      </div>
      <div>
        <Magic10Animation playedCards={playedCards} key={playedCards.length} />
        <WinAnimation
          isWon={game.state === "won"}
          isSinglePlayer={players.length === 1}
        />
        <GameOverAnimation isLost={game.state === "lost"} />
        <PlayerOrder
          players={players}
          activePlayerName={activePlayerName}
          isSpectating={isSpectating}
          spectating={spectating}
          spectators={game.spectators}
          onSpectatorChange={handleSpectatorChange}
        ></PlayerOrder>
        {players.length > 1 && activePlayerName && !isSpectating && (
          <Banners myTurn={isMyTurn} activePlayerName={activePlayerName} />
        )}
        <DndProvider backend={backendForDND}>
          <PileContainer
            availablePilesPerCard={availablePilesPerCard || {}}
            selectedCard={selectedCard}
            onCardPlayed={handleCardPlayed}
            cardsInDeck={getNumberOfCardsInDeck(game)}
            playedCards={playedCards}
            gameRule={gameRule}
            interestedPlayers={interestedPlayers}
            onShowInterest={(pileIndex) => onShowInterest(pileIndex)}
            gameId={game.id}
          />
          <CardContainer
            gameState={game.state}
            cards={cards}
            selectedCard={selectedCard}
            isMyTurn={isMyTurn && !isSpectating}
            gameRule={gameRule}
            isTimeLimitEnabled={isTimeLimitEnabled}
            handleCardIsBeingDragged={handleCardIsBeingDragged}
            handleCardIsNotBeingDragged={handleCardIsNotBeingDragged}
            handleAutomaticCardPlayed={handleAutomaticCardPlayed}
            gameId={game.id}
          />
          <DragPreview />
        </DndProvider>
        {!isSpectating && (
          <div style={{ paddingTop: "30px" }}>
            {getIsGameWithoutActivePlayer(game) ? (
              <Button onClick={onClaimTurnClick}>Claim turn</Button>
            ) : (
              <Button
                disabled={!isEndTurnButtonEnabled}
                onClick={onEndTurnClick}
              >
                End turn
              </Button>
            )}
          </div>
        )}
        {game.state === "started" && (
          <Button
            isDark
            isSmall={true}
            style={{ position: "absolute", right: "20px", bottom: "40px" }}
            onClick={() => window.location.reload()}
          >
            Return to start page
          </Button>
        )}
        {game.state === "started" && !isSpectating && (
          <Button
            isDark
            isSmall={true}
            style={{ position: "absolute", right: "20px", bottom: "20px" }}
            onClick={handleOnEndGameClick}
          >
            End game
          </Button>
        )}
      </div>

      {showConfirm && (
        <ConfirmPopup
          onSubmit={handleOnEndGameFoRealz}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {players.some((p) => !p.isConnected) && (
        <DisconnectedPlayersPopup
          onRemovePlayer={(name: string) => onRemovePlayer(game, name)}
          players={players}
        />
      )}
    </Board>
  );
};

export default GameBoard;
